import { ReactNode } from 'react';
import {
  AccessibilityRole,
  StyleProp,
  StyleSheet,
  Text as RNText,
  TextStyle,
} from 'react-native';

import { colors, fonts, fontSizes } from '@/constants/theme';
import { fitLineHeight } from '@/lib/textFit';

export type TextVariant =
  | 'body'
  | 'bodyMedium'
  | 'bodySemiBold'
  | 'bodyBold'
  | 'caption'
  | 'captionBold';

type ColorToken = keyof typeof colors;

export type TextProps = {
  children: ReactNode;
  /** Default `'body'` (Inter-Regular, 16). */
  variant?: TextVariant;
  /** Theme color token. Default `'text'`. */
  color?: ColorToken;
  align?: 'left' | 'center' | 'right' | 'justify';
  numberOfLines?: number;
  uppercase?: boolean;
  selectable?: boolean;
  /** Caps how far the iOS text size setting can grow this text. */
  maxFontSizeMultiplier?: number;
  /**
   * Inline tap handler. When set, the component also defaults
   * `accessibilityRole` to `'link'` so VoiceOver announces it as a
   * tappable element. Link styling (Deep Green bold) is opt-in via the
   * `link` boolean.
   */
  onPress?: () => void;
  /**
   * Apply link-style treatment (Deep Green + bold) to the text. Use
   * alongside `onPress` for inline tappable links inside a description.
   * No underline and no blue: Cool Blue on Frost fails contrast and read
   * as a web page, and nothing else in the app is underlined.
   */
  link?: boolean;
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  style?: StyleProp<TextStyle>;
  testID?: string;
};

export function Text({
  children,
  variant = 'body',
  color = 'text',
  align,
  numberOfLines,
  uppercase = false,
  selectable,
  maxFontSizeMultiplier,
  onPress,
  link = false,
  accessibilityRole,
  accessibilityLabel,
  style,
  testID,
}: TextProps) {
  const resolvedRole: AccessibilityRole | undefined =
    accessibilityRole ?? (onPress ? 'link' : undefined);

  return (
    <RNText
      style={fitLineHeight([
        styles[variant],
        {
          color: colors[color],
          textAlign: align,
          textTransform: uppercase ? 'uppercase' : undefined,
        },
        link && styles.link,
        style,
      ])}
      numberOfLines={numberOfLines}
      selectable={selectable}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      onPress={onPress}
      accessibilityRole={resolvedRole}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      {children}
    </RNText>
  );
}

const styles = StyleSheet.create({
  body: { fontFamily: fonts.body, fontSize: fontSizes.base, lineHeight: 22 },
  bodyMedium: { fontFamily: fonts.bodyMedium, fontSize: fontSizes.base, lineHeight: 22 },
  bodySemiBold: { fontFamily: fonts.bodySemiBold, fontSize: fontSizes.base, lineHeight: 22 },
  bodyBold: { fontFamily: fonts.bodyBold, fontSize: fontSizes.base, lineHeight: 22 },
  caption: { fontFamily: fonts.body, fontSize: fontSizes.sm, lineHeight: 18 },
  captionBold: { fontFamily: fonts.bodyBold, fontSize: fontSizes.sm, lineHeight: 18 },
  link: {
    color: colors.deepGreen,
    fontFamily: fonts.bodyBold,
  },
});
